import { doc, getDoc, setDoc, writeBatch } from "firebase/firestore";
import { MediaClip } from "../../../engine/clips";
import { CaptionsClip } from "../../../engine/clips/captions";
import { Timestamp } from "../../../engine/models";
import { db } from "../../../Firebase";
import { DEFAULT_LAYOUT_PACK_ID, formatLayoutRef } from "../../../librettoEditor/layouts/LayoutPacks";
import { createDetachedExportComposition } from "../../../librettoEditor/services/DetachedExportCompositionService";
import { CompositionPersistenceService } from "../../../librettoEditor/services/CompositionPersistenceService";
import { ScriptLayerStore } from "../../../librettoEditor/store/ScriptLayerStore";
import {
    kCompositionsCollectionName,
    kCompositionLayerClipsSubcollectionName,
    kCompositionLayersSubcollectionName,
} from "../../../utils/CollectionConstants";
import { CAPTION_PRESETS } from "../V2CaptionsSidebar";
import type { CreateClipsSettings } from "../CreateClipsSidebar";
import { type V2TranscriptWord } from "../V2TextEditor";
import { wordsToSentences } from "../V2TextEditor/utils";
import { buildDetachedExportSeed, type DetachedExportSeed } from "../decompose/buildDetachedExportSeed";
import { refineCreateClipRange } from "./createClipBoundaryRefinement";

export type TranscriptUnit = {
    id: string;
    startMs: number;
    endMs: number;
    text: string;
    speakerName?: string;
};

export type TranscriptTimingWord = {
    startMs: number;
    endMs: number;
    text: string;
    speakerName?: string;
};

export type GenerateClipsRequest = {
    compositionId: string;
    maxClips: number;
    clipDurationSec: number;
    topicPrompt?: string;
    transcriptUnits: TranscriptUnit[];
};

export type ClipCandidate = {
    id: string;
    title: string;
    description: string;
    startMs: number;
    endMs: number;
    startUnitId: string;
    endUnitId: string;
    previewText: string;
    score?: number;
};

export type GenerateClipsResponse = {
    candidates: ClipCandidate[];
};

export type PostAiCreateClipsFn = (
    args: GenerateClipsRequest,
) => Promise<GenerateClipsResponse | undefined>;

export type CreatedClipComposition = {
    id: string;
    compositionId: string;
    candidateId: string;
    title: string;
    description: string;
    durationSec: number;
    startMs: number;
    endMs: number;
    previewText: string;
    score?: number;
};

type ResolveAssetUrlFn = (
    assetId: string,
    type: "audio" | "video" | "image",
) => Promise<string | undefined>;

type ResolveEnhancedAudioUrlFn = (trackId: string) => Promise<string | undefined>;

type DuplicateCompositionFn = (args: {
    compositionId: string;
    titleOverride?: string;
}) => Promise<string>;

const SOCIAL_CLIP_CANVAS = { width: 1080, height: 1920 };
const MIN_CLIP_DURATION_SEC = 1;
const MAX_BATCH_WRITES = 450;
const BASE_CAPTION_CANVAS_HEIGHT = 1080;
const MIN_CAPTION_SCALE = 1;
const MAX_CAPTION_SCALE = 1.55;
const EPS = 1e-3;

const SOCIAL_CLIP_LAYOUT_MAP: Record<
    string,
    {
        captionPresetId: string;
        sceneLayoutId?: string;
    }
> = {
    social_clip_spotlight: { captionPresetId: "impact-yellow", sceneLayoutId: "zoom" },
    social_clip_captions: { captionPresetId: "karaoke-classic", sceneLayoutId: "captions" },
    social_clip_clean: { captionPresetId: "classic", sceneLayoutId: "camera" },
    social_clip_quote: { captionPresetId: "typewriter", sceneLayoutId: "chapter" },
    social_clip_punch_in: { captionPresetId: "bold-two-words", sceneLayoutId: "zoom" },
    social_clip_stack: { captionPresetId: "bold-yellow-highlight", sceneLayoutId: "media" },
    social_clip_reel: { captionPresetId: "impact-yellow", sceneLayoutId: "captions" },
    social_clip_podcast: { captionPresetId: "clean-paragraph", sceneLayoutId: "speaker" },
};

const clamp = (value: number, min: number, max: number): number => Math.max(min, Math.min(max, value));

const clampPositiveMs = (value: any): number => {
    const ms = Math.round(Number(value ?? 0));
    return Number.isFinite(ms) ? Math.max(0, ms) : 0;
};

const joinWordsAsText = (words: Array<{ text: string }>): string => {
    let out = "";
    const noSpaceBefore = (token: string): boolean => /^[.,!?;:)\]]$/.test(token);
    for (const word of words) {
        const text = String(word?.text ?? "").trim();
        if (!text) continue;
        if (out && !noSpaceBefore(text)) out += " ";
        out += text;
    }
    return out.trim();
};

const buildSentenceSpeakerName = (words: Array<V2TranscriptWord & { speaker?: string }>): string | undefined => {
    const speakerNames = words
        .map((word) => String(word?.speaker ?? "").trim())
        .filter(Boolean);
    if (speakerNames.length === 0) return undefined;
    const first = speakerNames[0];
    return speakerNames.every((speakerName) => speakerName === first) ? first : undefined;
};

const toSentenceWord = (word: V2TranscriptWord): (V2TranscriptWord & { speaker?: string }) | null => {
    const rawStartSec = Number(word?.startSec ?? 0);
    const rawEndSec = Number(word?.endSec ?? rawStartSec);
    if (!Number.isFinite(rawStartSec) || !Number.isFinite(rawEndSec)) return null;
    if (ScriptLayerStore.isTimeInTranscriptHiddenRange(rawStartSec)) return null;

    const startSec =
        ScriptLayerStore.mapSourceTimeToCompositionTimeForBoundary(rawStartSec) ??
        ScriptLayerStore.mapSourceTimeToCompositionTime(rawStartSec);
    const endSec =
        ScriptLayerStore.mapSourceTimeToCompositionTimeForBoundary(Math.max(rawStartSec, rawEndSec)) ??
        ScriptLayerStore.mapSourceTimeToCompositionTime(Math.max(rawStartSec, rawEndSec));
    if (!Number.isFinite(startSec) || !Number.isFinite(endSec)) return null;

    const correctedText =
        ScriptLayerStore.getWordCorrectionText({
            sourceIndex: word?.sourceIndex,
            srcStartSec: word?.srcStartSec,
            srcEndSec: word?.srcEndSec,
        }) ?? null;

    const text = String(correctedText ?? word?.text ?? "").trim();
    if (!text) return null;

    return {
        ...word,
        text,
        startSec: Math.max(0, Number(startSec)),
        endSec: Math.max(Number(startSec), Number(endSec)),
        speaker: typeof word?.speaker === "string" ? word.speaker : undefined,
    };
};

export const buildEditedTranscriptUnitsForCreateClips = (): TranscriptUnit[] => {
    const mappedWords = (ScriptLayerStore.visibleTranscriptWords ?? [])
        .map((word) => toSentenceWord(word))
        .filter((word): word is V2TranscriptWord & { speaker?: string } => Boolean(word))
        .sort((a, b) => Number(a.startSec ?? 0) - Number(b.startSec ?? 0));

    if (mappedWords.length === 0) return [];

    const sentences = wordsToSentences(mappedWords);
    const units: TranscriptUnit[] = [];

    for (let index = 0; index < sentences.length; index += 1) {
        const sentence = sentences[index];
        const sentenceWords = Array.isArray(sentence?.words) ? sentence.words : [];
        if (sentenceWords.length === 0) continue;

        const text = joinWordsAsText(sentenceWords);
        if (!text) continue;

        const startMs = Math.max(0, Math.round(Number(sentence.startSec ?? 0) * 1000));
        const endMs = Math.max(startMs, Math.round(Number(sentence.endSec ?? sentence.startSec ?? 0) * 1000));
        const speakerName = buildSentenceSpeakerName(sentenceWords as Array<V2TranscriptWord & { speaker?: string }>);

        units.push({
            id: `unit_${index + 1}`,
            startMs,
            endMs,
            text,
            ...(speakerName ? { speakerName } : {}),
        });
    }

    return units;
};

export const buildEditedTranscriptWordsForCreateClips = (): TranscriptTimingWord[] =>
    (ScriptLayerStore.visibleTranscriptWords ?? [])
        .map((word) => toSentenceWord(word))
        .filter((word): word is V2TranscriptWord & { speaker?: string } => Boolean(word))
        .sort((a, b) => Number(a.startSec ?? 0) - Number(b.startSec ?? 0))
        .map((word) => ({
            startMs: Math.max(0, Math.round(Number(word.startSec ?? 0) * 1000)),
            endMs: Math.max(0, Math.round(Number(word.endSec ?? word.startSec ?? 0) * 1000)),
            text: String(word.text ?? "").trim(),
            ...(word.speaker ? { speakerName: String(word.speaker) } : {}),
        }))
        .filter((word) => Boolean(word.text) && Number.isFinite(word.endMs) && word.endMs >= word.startMs);

const getScriptLayerClip = (comp: any): any | null => {
    try {
        const scriptClipId = String(comp?.scriptLayer?.clipId ?? "");
        if (!scriptClipId || typeof comp?.getClipById !== "function") return null;
        return comp.getClipById(scriptClipId) ?? null;
    } catch {
        return null;
    }
};

const getScriptEndSec = (comp: any): number | null => {
    const scriptClip = getScriptLayerClip(comp);
    if (!scriptClip) return null;
    const startSec = Number(scriptClip?.start?.seconds ?? 0);
    const durationSec = Number(scriptClip?.timelineDuration?.seconds ?? 0);
    if (!Number.isFinite(startSec) || !Number.isFinite(durationSec)) return null;
    return startSec + durationSec;
};

const compositionHasVisualContent = (comp: any): boolean => {
    const scriptClip = getScriptLayerClip(comp);
    const scriptHasVideo =
        scriptClip && typeof scriptClip.getVideoSources === "function"
            ? (scriptClip.getVideoSources?.()?.length ?? 0) > 0
            : false;
    if (scriptHasVideo) return true;

    for (const track of comp?.tracks ?? []) {
        for (const clip of track?.clips ?? []) {
            const type = String(clip?.type ?? "").toLowerCase();
            if (type === "video" || type === "image" || type === "captions" || type === "subtitles") {
                return true;
            }
        }
    }

    return false;
};

const deleteClipFromComposition = (comp: any, clipId: string): void => {
    const clip = typeof comp?.getClipById === "function" ? comp.getClipById(clipId) : null;
    const track = clip?.track;
    if (!clip || !track || typeof track.remove !== "function") return;
    try {
        track.remove(clip);
    } catch {}
};

const trimDetachedMediaClipLeft = (clip: any, deltaSec: number): void => {
    if (!clip || !(clip instanceof MediaClip) || Math.abs(deltaSec) < 1e-6) return;

    const rate = Number(clip.playbackRate ?? 1) || 1;
    const currentStart = Number(clip?.start?.seconds ?? 0);
    const currentVisibleSrcDur = Array.isArray(clip?.segments) && clip.segments.length > 0
        ? clip.segments.reduce(
              (sum: number, seg: any) => sum + Math.max(0, Number(seg?.srcEndSec ?? 0) - Number(seg?.srcStartSec ?? 0)),
              0,
          )
        : Math.max(
              0,
              Number(clip?.range?.[1]?.seconds ?? 0) - Number(clip?.range?.[0]?.seconds ?? 0),
          );

    const minTimelineDur = 0.01;
    const minSrcDur = minTimelineDur * rate;
    const currentInSec = Number(clip?.range?.[0]?.seconds ?? 0);

    let effectiveDeltaSec = deltaSec;
    if (deltaSec > 0) {
        const maxExtend = currentInSec / rate;
        effectiveDeltaSec = Math.min(deltaSec, maxExtend);
    }

    const desiredStart = Math.max(0, currentStart - effectiveDeltaSec);
    const desiredVisibleSrcDur = Math.max(minSrcDur, currentVisibleSrcDur + effectiveDeltaSec * rate);

    const sourceTimeAtVisibleOffset = (visibleOffsetSrcSec: number): number => {
        let remaining = Math.max(0, visibleOffsetSrcSec);
        const segments = Array.isArray(clip?.segments) ? clip.segments : [];
        for (const seg of segments) {
            const segmentStart = Number(seg?.srcStartSec ?? 0);
            const segmentEnd = Number(seg?.srcEndSec ?? segmentStart);
            const segmentDuration = Math.max(0, segmentEnd - segmentStart);
            if (remaining <= segmentDuration + 1e-6) return segmentStart + remaining;
            remaining -= segmentDuration;
        }
        return Number(clip?.range?.[1]?.seconds ?? currentInSec);
    };

    let newInSec = currentInSec;
    if (desiredVisibleSrcDur >= currentVisibleSrcDur) {
        const extendSrc = desiredVisibleSrcDur - currentVisibleSrcDur;
        newInSec = Math.max(0, currentInSec - extendSrc);
    } else {
        const removeSrc = Math.min(
            currentVisibleSrcDur - minSrcDur,
            currentVisibleSrcDur - desiredVisibleSrcDur,
        );
        newInSec = sourceTimeAtVisibleOffset(removeSrc);
    }

    const desiredOffset = desiredStart - newInSec;
    try {
        (clip as any)._offset.seconds = desiredOffset;
    } catch {
        (clip as any)._offset = Timestamp.fromSeconds(desiredOffset);
    }
    if (Array.isArray(clip.range) && clip.range[0]) {
        clip.range[0].seconds = newInSec;
    }
    try {
        clip.normalizeCuts?.();
    } catch {}
    try {
        clip.deriveSegments?.();
    } catch {}
    try {
        clip.trigger?.("frame", undefined);
    } catch {}
};

const trimDetachedMediaClipRight = (clip: any, deltaSec: number): void => {
    if (!clip || !(clip instanceof MediaClip) || Math.abs(deltaSec) < 1e-6) return;

    const rate = Number(clip.playbackRate ?? 1) || 1;
    const currentVisibleSrcDur = Array.isArray(clip?.segments) && clip.segments.length > 0
        ? clip.segments.reduce(
              (sum: number, seg: any) => sum + Math.max(0, Number(seg?.srcEndSec ?? 0) - Number(seg?.srcStartSec ?? 0)),
              0,
          )
        : Math.max(
              0,
              Number(clip?.range?.[1]?.seconds ?? 0) - Number(clip?.range?.[0]?.seconds ?? 0),
          );
    const minTimelineDur = 0.01;
    const minSrcDur = minTimelineDur * rate;
    const currentOutSec = Number(clip?.range?.[1]?.seconds ?? 0);
    const sourceDurationSec = Number(clip?.duration?.seconds ?? currentOutSec);

    let effectiveDeltaSec = deltaSec;
    if (deltaSec > 0) {
        const maxExtend = Math.max(0, sourceDurationSec - currentOutSec) / rate;
        effectiveDeltaSec = Math.min(deltaSec, maxExtend);
    }

    const desiredVisibleSrcDur = Math.max(minSrcDur, currentVisibleSrcDur + effectiveDeltaSec * rate);

    const sourceTimeAtVisibleOffset = (visibleOffsetSrcSec: number): number => {
        let remaining = Math.max(0, visibleOffsetSrcSec);
        const segments = Array.isArray(clip?.segments) ? clip.segments : [];
        for (const seg of segments) {
            const segmentStart = Number(seg?.srcStartSec ?? 0);
            const segmentEnd = Number(seg?.srcEndSec ?? segmentStart);
            const segmentDuration = Math.max(0, segmentEnd - segmentStart);
            if (remaining <= segmentDuration + 1e-6) return segmentStart + remaining;
            remaining -= segmentDuration;
        }
        return Number(clip?.range?.[1]?.seconds ?? 0);
    };

    let newOutSec = currentOutSec;
    if (desiredVisibleSrcDur > currentVisibleSrcDur) {
        const extendSrc = desiredVisibleSrcDur - currentVisibleSrcDur;
        newOutSec = Math.min(sourceDurationSec, currentOutSec + extendSrc);
    } else {
        newOutSec = sourceTimeAtVisibleOffset(desiredVisibleSrcDur);
    }

    if (Array.isArray(clip.range) && clip.range[1]) {
        clip.range[1].seconds = newOutSec;
    }
    try {
        clip.normalizeCuts?.();
    } catch {}
    try {
        clip.deriveSegments?.();
    } catch {}
    try {
        clip.trigger?.("frame", undefined);
    } catch {}
};

const trimDetachedClipLeft = (clip: any, deltaSec: number): void => {
    if (!clip || Math.abs(deltaSec) < 1e-6) return;

    if (clip instanceof MediaClip) {
        trimDetachedMediaClipLeft(clip, deltaSec);
        return;
    }

    if (!("start" in clip)) return;
    try {
        const currentStart = Number(clip?.start?.seconds ?? 0);
        clip.start = Timestamp.fromSeconds(currentStart - deltaSec);
    } catch {}
    try {
        clip.trigger?.("frame", undefined);
    } catch {}
};

const trimDetachedClipRight = (clip: any, deltaSec: number): void => {
    if (!clip || Math.abs(deltaSec) < 1e-6) return;

    if (clip instanceof MediaClip) {
        trimDetachedMediaClipRight(clip, deltaSec);
        return;
    }

    if (!("stop" in clip)) return;
    try {
        const currentStop = Number(clip?.stop?.seconds ?? 0);
        clip.stop = Timestamp.fromSeconds(currentStop + deltaSec);
    } catch {}
    try {
        clip.trigger?.("frame", undefined);
    } catch {}
};

const trimDetachedCompositionToRange = (comp: any, rangeStartSec: number, rangeEndSec: number): boolean => {
    const scriptClip: any = getScriptLayerClip(comp);
    if (!scriptClip || String(scriptClip?.type ?? "") !== "script") return false;

    const scriptEnd = getScriptEndSec(comp);
    if (scriptEnd == null || !Number.isFinite(scriptEnd) || scriptEnd <= 0) return false;

    const rawStart = Number(rangeStartSec ?? 0);
    const rawEnd = Number(rangeEndSec ?? 0);
    if (!Number.isFinite(rawStart) || !Number.isFinite(rawEnd)) return false;

    const start = Math.max(0, Math.min(scriptEnd, Math.min(rawStart, rawEnd)));
    const end = Math.max(start, Math.min(scriptEnd, Math.max(rawStart, rawEnd)));
    if (end - start <= EPS) return false;

    const otherClipIds: string[] = [];
    for (const track of comp?.tracks ?? []) {
        for (const clip of track?.clips ?? []) {
            if (!clip) continue;
            if (String(clip?.type ?? "") === "script" || String(clip?.id ?? "") === String(scriptClip?.id ?? "")) {
                continue;
            }
            otherClipIds.push(String(clip.id));
        }
    }

    for (const clipId of otherClipIds) {
        const clip: any = typeof comp?.getClipById === "function" ? comp.getClipById(clipId) : null;
        if (!clip) continue;
        if (clip instanceof MediaClip) {
            const offsetSec = Number((clip as any)._offset?.seconds ?? clip?.offset?.seconds ?? 0);
            const nextOffset = offsetSec - start;
            try {
                (clip as any)._offset.seconds = nextOffset;
            } catch {
                (clip as any)._offset = Timestamp.fromSeconds(nextOffset);
            }
            try {
                clip.deriveSegments?.();
            } catch {}
            try {
                clip.trigger?.("frame", undefined);
            } catch {}
            continue;
        }
        if (typeof clip?.offsetBy === "function") {
            try {
                clip.offsetBy(Timestamp.fromSeconds(-start));
            } catch {}
        }
    }

    const segments: any[] = Array.isArray(scriptClip?.scriptSegments) ? scriptClip.scriptSegments : [];
    const clipInSec = Number(scriptClip?.range?.[0]?.seconds ?? 0);
    const clipOutSec = Number(
        scriptClip?.range?.[1]?.seconds ?? scriptClip?.duration?.seconds ?? clipInSec,
    );

    const getSegEnd = (seg: any): number => {
        const sourceStartSec = Number(seg?.srcStartSec ?? Number.NaN);
        const sourceEndSec = Number(seg?.srcEndSec ?? Number.NaN);
        const compStartSec = Number(seg?.compStartSec ?? Number.NaN);
        if (!Number.isFinite(sourceStartSec) || !Number.isFinite(sourceEndSec) || !Number.isFinite(compStartSec)) {
            return Number.NaN;
        }
        return compStartSec + Math.max(0, sourceEndSec - sourceStartSec);
    };

    segments.sort((a, b) => Number(a?.compStartSec ?? 0) - Number(b?.compStartSec ?? 0));

    const trailingInsertionDurationSec = (): number => {
        let total = 0;
        for (const insertion of (scriptClip?.gapInsertions ?? []) as any[]) {
            const atSec = Number(insertion?.atSec ?? Number.NaN);
            const durationSec = Number(insertion?.durationSec ?? 0);
            if (!Number.isFinite(atSec) || !Number.isFinite(durationSec) || durationSec <= EPS) continue;
            if (Math.abs(atSec - clipOutSec) <= EPS) total += durationSec;
        }
        return total;
    };

    type LocatedTime =
        | { kind: "segment"; localSec: number }
        | { kind: "hole"; atSec: number; holeStartCompSec: number; holeEndCompSec: number };

    const locate = (compTimeSec: number): LocatedTime => {
        const t = Number(compTimeSec);
        if (!Number.isFinite(t) || segments.length === 0) {
            return {
                kind: "segment",
                localSec: Math.max(clipInSec, Math.min(clipOutSec, t)),
            };
        }

        const firstSegment = segments[0];
        const firstCompStart = Number(firstSegment?.compStartSec ?? Number.NaN);
        const firstLocalStart = Number(firstSegment?.srcStartSec ?? clipInSec);
        if (Number.isFinite(firstCompStart) && t < firstCompStart - EPS) {
            return {
                kind: "hole",
                atSec: firstLocalStart,
                holeStartCompSec: Number(scriptClip?.start?.seconds ?? 0),
                holeEndCompSec: firstCompStart,
            };
        }

        for (let index = 0; index < segments.length; index += 1) {
            const segment = segments[index];
            const sourceStartSec = Number(segment?.srcStartSec ?? Number.NaN);
            const sourceEndSec = Number(segment?.srcEndSec ?? Number.NaN);
            const compStartSec = Number(segment?.compStartSec ?? Number.NaN);
            if (!Number.isFinite(sourceStartSec) || !Number.isFinite(sourceEndSec) || !Number.isFinite(compStartSec)) {
                continue;
            }
            const segmentEndCompSec = compStartSec + Math.max(0, sourceEndSec - sourceStartSec);
            if (t >= compStartSec - EPS && t < segmentEndCompSec - EPS) {
                return {
                    kind: "segment",
                    localSec: sourceStartSec + (t - compStartSec),
                };
            }

            const nextSegment = segments[index + 1];
            if (!nextSegment) continue;
            const nextCompStartSec = Number(nextSegment?.compStartSec ?? Number.NaN);
            if (!Number.isFinite(nextCompStartSec)) continue;
            if (t >= segmentEndCompSec - EPS && t < nextCompStartSec - EPS) {
                return {
                    kind: "hole",
                    atSec: Number(nextSegment?.srcStartSec ?? sourceEndSec),
                    holeStartCompSec: segmentEndCompSec,
                    holeEndCompSec: nextCompStartSec,
                };
            }
        }

        const lastSegment = segments[segments.length - 1];
        const lastEndCompSec = getSegEnd(lastSegment);
        const trailingInsertionSec = trailingInsertionDurationSec();
        if (
            Number.isFinite(lastEndCompSec) &&
            trailingInsertionSec > EPS &&
            t >= lastEndCompSec - EPS &&
            t < lastEndCompSec + trailingInsertionSec + EPS
        ) {
            return {
                kind: "hole",
                atSec: clipOutSec,
                holeStartCompSec: lastEndCompSec,
                holeEndCompSec: lastEndCompSec + trailingInsertionSec,
            };
        }

        return { kind: "segment", localSec: clipOutSec };
    };

    const startLoc = locate(start);
    const endLoc = locate(end);
    const sliceStartLocal = Math.max(
        clipInSec,
        Math.min(clipOutSec, startLoc.kind === "segment" ? startLoc.localSec : startLoc.atSec),
    );
    const sliceEndLocal = Math.max(
        sliceStartLocal,
        Math.min(clipOutSec, endLoc.kind === "segment" ? endLoc.localSec : endLoc.atSec),
    );
    const leadingHoleRemainingSec =
        startLoc.kind === "hole" ? Math.max(0, Number(startLoc.holeEndCompSec ?? 0) - start) : 0;
    const trailingHolePrefixSec =
        endLoc.kind === "hole" ? Math.max(0, end - Number(endLoc.holeStartCompSec ?? 0)) : 0;

    try {
        const nextInsertions: any[] = [];
        for (const insertion of (scriptClip?.gapInsertions ?? []) as any[]) {
            const atSec = Number(insertion?.atSec ?? Number.NaN);
            const durationSec = Number(insertion?.durationSec ?? 0);
            if (!Number.isFinite(atSec) || !Number.isFinite(durationSec) || durationSec <= EPS) continue;
            if (atSec < sliceStartLocal - EPS) continue;
            if (atSec > sliceEndLocal + EPS) continue;
            nextInsertions.push({ ...insertion, atSec, durationSec });
        }
        scriptClip.gapInsertions = nextInsertions;
    } catch {}

    if (leadingHoleRemainingSec > EPS) {
        const atSec = sliceStartLocal;
        const existingInsertion = (scriptClip?.gapInsertions ?? []).find(
            (insertion: any) => Math.abs(Number(insertion?.atSec ?? Number.NaN) - atSec) <= EPS,
        );
        if (existingInsertion) {
            existingInsertion.durationSec = leadingHoleRemainingSec;
        } else {
            (scriptClip?.gapInsertions ?? []).push({
                id: crypto?.randomUUID?.() ?? `gapins_${Date.now()}_${Math.random()}`,
                atSec,
                durationSec: leadingHoleRemainingSec,
            });
        }
    }

    const isTrailingInsertionHole =
        endLoc.kind === "hole" && Math.abs(Number(endLoc?.atSec ?? Number.NaN) - clipOutSec) <= EPS;
    if (isTrailingInsertionHole && trailingHolePrefixSec > EPS) {
        const existingInsertion = (scriptClip?.gapInsertions ?? []).find(
            (insertion: any) => Math.abs(Number(insertion?.atSec ?? Number.NaN) - clipOutSec) <= EPS,
        );
        if (existingInsertion) {
            existingInsertion.durationSec = trailingHolePrefixSec;
        } else {
            (scriptClip?.gapInsertions ?? []).push({
                id: crypto?.randomUUID?.() ?? `gapins_${Date.now()}_${Math.random()}`,
                atSec: clipOutSec,
                durationSec: trailingHolePrefixSec,
            });
        }
    }

    try {
        (scriptClip as any).normalizeGapInsertions?.();
    } catch {}

    const cutScriptRange = (cutStartSec: number, cutEndSec: number): void => {
        if (cutEndSec - cutStartSec <= EPS) return;
        const tolerance = 0.05;
        const splitsToRemove = (scriptClip?.splits ?? []).filter((split: any) => {
            const splitTime = Number(split?.timeSec ?? Number.NaN);
            if (!Number.isFinite(splitTime)) return false;
            return (
                Math.abs(splitTime - cutStartSec) < tolerance ||
                Math.abs(splitTime - cutEndSec) < tolerance ||
                (splitTime > cutStartSec && splitTime < cutEndSec)
            );
        });
        for (const split of splitsToRemove) {
            try {
                scriptClip.removeScriptSplit?.(split.id);
            } catch {}
        }
        try {
            scriptClip.addScriptCut?.(cutStartSec, cutEndSec, undefined, "delete");
        } catch {}
    };

    if (sliceStartLocal > clipInSec + EPS) {
        cutScriptRange(clipInSec, sliceStartLocal);
    }
    if (sliceEndLocal < clipOutSec - EPS) {
        cutScriptRange(sliceEndLocal, clipOutSec);
    }

    if (endLoc.kind === "hole" && !isTrailingInsertionHole && trailingHolePrefixSec > EPS) {
        try {
            scriptClip.gapInsertions = (scriptClip?.gapInsertions ?? []).filter(
                (insertion: any) => Number(insertion?.atSec ?? 0) <= sliceEndLocal + EPS,
            );
        } catch {}
        try {
            (scriptClip as any).normalizeGapInsertions?.();
        } catch {}
        try {
            scriptClip.insertEmptyScene?.(sliceEndLocal, trailingHolePrefixSec);
        } catch {}
    }

    try {
        scriptClip.normalizeScriptCuts?.();
    } catch {}
    try {
        scriptClip.normalizeGaps?.();
    } catch {}
    try {
        (scriptClip as any).normalizeGapInsertions?.();
    } catch {}
    try {
        scriptClip.normalizeSplits?.();
    } catch {}
    try {
        scriptClip.invalidateScenesCache?.();
    } catch {}
    try {
        scriptClip.deriveSegments?.();
    } catch {}
    try {
        scriptClip.trigger?.("frame", undefined);
    } catch {}

    const newScriptEnd = getScriptEndSec(comp);
    const newEndSec = newScriptEnd != null && Number.isFinite(newScriptEnd) ? newScriptEnd : end - start;

    for (const clipId of otherClipIds) {
        let clip: any = typeof comp?.getClipById === "function" ? comp.getClipById(clipId) : null;
        if (!clip) continue;

        let clipStartSec = Number(clip?.start?.seconds ?? 0);
        let durationSec = Number(clip?.timelineDuration?.seconds ?? Number.NaN);
        if (!Number.isFinite(durationSec)) {
            const clipStopSec = Number(clip?.stop?.seconds ?? clipStartSec);
            durationSec = Math.max(0, clipStopSec - clipStartSec);
        }
        let clipEndSec = clipStartSec + durationSec;

        if (clipEndSec <= EPS || clipStartSec >= newEndSec - EPS) {
            deleteClipFromComposition(comp, clipId);
            continue;
        }

        if (clipStartSec < 0) {
            trimDetachedClipLeft(clip, clipStartSec);
            clip = typeof comp?.getClipById === "function" ? comp.getClipById(clipId) : null;
            if (!clip) continue;
            clipStartSec = Number(clip?.start?.seconds ?? 0);
            durationSec = Number(clip?.timelineDuration?.seconds ?? durationSec);
            if (!Number.isFinite(durationSec)) {
                const clipStopSec = Number(clip?.stop?.seconds ?? clipStartSec);
                durationSec = Math.max(0, clipStopSec - clipStartSec);
            }
            clipEndSec = clipStartSec + durationSec;
            if (clipEndSec <= EPS) {
                deleteClipFromComposition(comp, clipId);
                continue;
            }
        }

        if (clipEndSec > newEndSec + EPS) {
            const desiredDurationSec = Math.max(0, newEndSec - clipStartSec);
            const deltaSec = desiredDurationSec - durationSec;
            trimDetachedClipRight(clip, deltaSec);
            clip = typeof comp?.getClipById === "function" ? comp.getClipById(clipId) : null;
            if (!clip) continue;
            durationSec = Number(clip?.timelineDuration?.seconds ?? durationSec);
            if (!Number.isFinite(durationSec)) {
                const clipStopSec = Number(clip?.stop?.seconds ?? clipStartSec);
                durationSec = Math.max(0, clipStopSec - clipStartSec);
            }
            if (Number.isFinite(durationSec) && durationSec <= 0.01) {
                deleteClipFromComposition(comp, clipId);
            }
        }
    }

    try {
        comp.recalculateDuration?.();
    } catch {}
    try {
        void comp.onClipTimingChanged?.();
    } catch {}

    return true;
};

const getCaptionScaleForComposition = (composition: any): number => {
    const compositionHeight = Number(composition?.height ?? BASE_CAPTION_CANVAS_HEIGHT);
    if (!Number.isFinite(compositionHeight) || compositionHeight <= 0) return 1;
    return clamp(compositionHeight / BASE_CAPTION_CANVAS_HEIGHT, MIN_CAPTION_SCALE, MAX_CAPTION_SCALE);
};

const scaleCaptionTextStyle = (textStyle: any, scale: number): any => {
    if (!textStyle || typeof textStyle !== "object") return textStyle;
    const next = { ...textStyle };
    if (typeof next.fontSize === "number") {
        next.fontSize = Math.max(30, Math.round(next.fontSize * scale));
    }
    if (next.stroke) {
        next.stroke = {
            ...next.stroke,
            width: Math.max(1, Math.round(Number(next.stroke.width ?? 1) * scale)),
        };
    }
    if (next.shadow) {
        next.shadow = {
            ...next.shadow,
            blur: Math.max(0, Number(next.shadow.blur ?? 0) * scale),
            distance: Number(next.shadow.distance ?? 0) * scale,
        };
    }
    return next;
};

const scaleCaptionHighlightStyle = (highlightStyle: any, scale: number): any => {
    if (!highlightStyle || typeof highlightStyle !== "object") return highlightStyle;
    const next = { ...highlightStyle };
    if (next.stroke) {
        next.stroke = {
            ...next.stroke,
            width: Math.max(1, Math.round(Number(next.stroke.width ?? 1) * scale)),
        };
    }
    if (next.background) {
        next.background = {
            ...next.background,
            paddingX: Number.isFinite(Number(next.background.paddingX))
                ? Math.max(0, Math.round(Number(next.background.paddingX) * scale))
                : next.background.paddingX,
            paddingY: Number.isFinite(Number(next.background.paddingY))
                ? Math.max(0, Math.round(Number(next.background.paddingY) * scale))
                : next.background.paddingY,
            borderRadius: Number.isFinite(Number(next.background.borderRadius))
                ? Math.max(0, Math.round(Number(next.background.borderRadius) * scale))
                : next.background.borderRadius,
        };
    }
    return next;
};

const scaleCaptionBackground = (background: any, scale: number): any => {
    if (!background || typeof background !== "object") return background;
    const next = { ...background };
    if (typeof next.borderRadius === "number") {
        next.borderRadius = Math.max(0, Math.round(next.borderRadius * scale));
    }
    if (next.padding) {
        next.padding = {
            x: Math.max(0, Math.round(Number(next.padding.x ?? 0) * scale)),
            y: Math.max(0, Math.round(Number(next.padding.y ?? 0) * scale)),
        };
    }
    return next;
};

const applySocialClipTemplate = async (
    comp: any,
    settings: CreateClipsSettings,
): Promise<void> => {
    if (!settings.useLayout) return;

    const mapping = SOCIAL_CLIP_LAYOUT_MAP[String(settings.layoutId ?? "")] ?? SOCIAL_CLIP_LAYOUT_MAP.social_clip_spotlight;
    const preset = CAPTION_PRESETS.find((entry) => entry.id === mapping.captionPresetId) ?? CAPTION_PRESETS[0];
    if (!preset) return;

    try {
        comp.resize?.(SOCIAL_CLIP_CANVAS.width, SOCIAL_CLIP_CANVAS.height);
    } catch {}

    const scriptClip = getScriptLayerClip(comp);
    if (!scriptClip) return;
    const shouldIncludeCaptions = compositionHasVisualContent(comp);

    const scale = getCaptionScaleForComposition(comp);
    const startSec = Number(scriptClip?.start?.seconds ?? 0);
    const stopSec = Number(getScriptEndSec(comp) ?? startSec);

    const applyPresetToCaptionsClip = (captionsClip: any): void => {
        if (!captionsClip) return;
        try {
            captionsClip.scriptClipId = scriptClip.id;
        } catch {}
        try {
            captionsClip.timingScope = "all";
        } catch {}
        try {
            captionsClip.displayMode = preset.displayMode;
        } catch {}
        try {
            captionsClip.textCase = preset.textCase;
        } catch {}
        try {
            captionsClip.textAlign = preset.textAlign ?? "center";
        } catch {}
        try {
            captionsClip.maxWidth = preset.maxWidth ?? "80%";
        } catch {}
        try {
            captionsClip.textStyle = scaleCaptionTextStyle(preset.textStyle, scale);
        } catch {}
        try {
            captionsClip.highlightStyle = scaleCaptionHighlightStyle(preset.highlightStyle, scale);
        } catch {}
        try {
            captionsClip.background = scaleCaptionBackground((preset as any).background, scale);
        } catch {}
        try {
            captionsClip.phraseConfig = preset.phraseConfig;
        } catch {}
        try {
            captionsClip.position = { x: "50%", y: "85%" };
        } catch {}
        try {
            captionsClip.start = Timestamp.fromSeconds(startSec);
        } catch {}
        try {
            captionsClip.stop = Timestamp.fromSeconds(stopSec);
        } catch {}
        try {
            captionsClip.trigger?.("frame", undefined);
        } catch {}
    };

    const captionsClips: any[] = [];
    if (shouldIncludeCaptions) {
        for (const track of comp?.tracks ?? []) {
            for (const clip of track?.clips ?? []) {
                if (clip && String(clip?.type ?? "") === "captions") captionsClips.push(clip);
            }
        }
    }

    if (shouldIncludeCaptions && captionsClips.length === 0) {
        const captionsClip = new CaptionsClip({
            scriptClipId: scriptClip.id,
            timingScope: "all",
            displayMode: preset.displayMode,
            textCase: preset.textCase,
            textAlign: preset.textAlign,
            maxWidth: preset.maxWidth,
            textStyle: scaleCaptionTextStyle(preset.textStyle, scale),
            highlightStyle: scaleCaptionHighlightStyle(preset.highlightStyle, scale),
            background: scaleCaptionBackground((preset as any).background, scale),
            phraseConfig: preset.phraseConfig,
            position: { x: "50%", y: "85%" },
            start: Timestamp.fromSeconds(startSec),
            stop: Timestamp.fromSeconds(stopSec),
            words: [],
        } as any);
        await comp.add(captionsClip as any);
        captionsClips.push(captionsClip);
    }

    if (shouldIncludeCaptions) {
        for (const captionsClip of captionsClips) {
            applyPresetToCaptionsClip(captionsClip);
        }
    }

    if (mapping.sceneLayoutId) {
        const layoutRef = formatLayoutRef(DEFAULT_LAYOUT_PACK_ID, mapping.sceneLayoutId);
        const scenes: any[] = typeof scriptClip.getScenes === "function" ? scriptClip.getScenes() : [];
        for (const scene of scenes) {
            const sceneIndex = Number(scene?.index ?? Number.NaN);
            if (!Number.isFinite(sceneIndex) || sceneIndex < 0) continue;
            try {
                scriptClip.setSceneLayout?.(sceneIndex, layoutRef, "vertical");
            } catch {}
        }
    }

    try {
        scriptClip.invalidateScenesCache?.();
    } catch {}
    try {
        scriptClip.deriveSegments?.();
    } catch {}
    try {
        comp.recalculateDuration?.();
    } catch {}
}

type ExistingLayerRecord = {
    layerId: string;
    data: any;
    clipIds: string[];
    clipsById: Map<string, any>;
};

const fetchExistingCompositionLayers = async (compositionId: string): Promise<ExistingLayerRecord[]> => {
    const compRef = doc(db, kCompositionsCollectionName, compositionId);
    const compSnap = await getDoc(compRef);
    if (!compSnap.exists()) return [];

    const compData = compSnap.data() || {};
    const orderedLayerIds: string[] = Array.isArray(compData?.orderedLayerIds)
        ? compData.orderedLayerIds.map((id: any) => String(id ?? "")).filter(Boolean)
        : [];
    const layers: ExistingLayerRecord[] = [];

    for (const layerId of orderedLayerIds) {
        const layerRef = doc(compRef, kCompositionLayersSubcollectionName, layerId);
        const layerSnap = await getDoc(layerRef);
        if (!layerSnap.exists()) continue;

        const layerData = layerSnap.data() || {};
        const orderedClipIds: string[] = Array.isArray(layerData?.orderedClipIds)
            ? layerData.orderedClipIds.map((id: any) => String(id ?? "")).filter(Boolean)
            : [];
        const clipsById = new Map<string, any>();
        for (const clipId of orderedClipIds) {
            const clipRef = doc(layerRef, kCompositionLayerClipsSubcollectionName, clipId);
            const clipSnap = await getDoc(clipRef);
            if (!clipSnap.exists()) continue;
            clipsById.set(clipId, clipSnap.data() || {});
        }

        layers.push({
            layerId,
            data: layerData,
            clipIds: orderedClipIds,
            clipsById,
        });
    }

    return layers;
};

type DetachedSnapshotLayer = {
    layerId: string;
    name: string | null;
    type: string;
    orderedClipIds: string[];
    clipRecordsById: Map<string, any>;
};

const buildDetachedSnapshot = (comp: any): {
    canvasWidth: number;
    canvasHeight: number;
    backgroundColor: string;
    orderedLayerIds: string[];
    layers: DetachedSnapshotLayer[];
} => {
    const layers: DetachedSnapshotLayer[] = [];

    for (const track of comp?.tracks ?? []) {
        const layerId = String(track?.id ?? "");
        if (!layerId) continue;

        const orderedClipIds: string[] = [];
        const clipRecordsById = new Map<string, any>();

        for (const clip of track?.clips ?? []) {
            if (!clip) continue;
            const clipId = String(clip?.id ?? "");
            if (!clipId) continue;
            orderedClipIds.push(clipId);
            const serialized = CompositionPersistenceService.serializeClipForExportSnapshot(clip, { layerId });
            if (serialized) clipRecordsById.set(clipId, serialized);
        }

        layers.push({
            layerId,
            name: typeof (track as any)?.name === "string" ? String((track as any).name) : null,
            type: typeof (track as any)?.type === "string" && (track as any).type
                ? String((track as any).type)
                : "base",
            orderedClipIds,
            clipRecordsById,
        });
    }

    return {
        canvasWidth: Number(comp?.settings?.width ?? comp?.width ?? 1920),
        canvasHeight: Number(comp?.settings?.height ?? comp?.height ?? 1080),
        backgroundColor: String(comp?.settings?.background ?? comp?.background ?? "#000000"),
        orderedLayerIds: layers.map((layer) => layer.layerId).reverse(),
        layers,
    };
};

const persistDetachedSnapshotToComposition = async (args: {
    compositionId: string;
    comp: any;
}): Promise<void> => {
    const compositionId = String(args.compositionId ?? "").trim();
    if (!compositionId) throw new Error("Missing compositionId for clip persistence");

    const compRef = doc(db, kCompositionsCollectionName, compositionId);
    const compSnap = await getDoc(compRef);
    if (!compSnap.exists()) throw new Error("Target composition not found");

    const parentData = compSnap.data() || {};
    const existingLayers = await fetchExistingCompositionLayers(compositionId);
    const existingLayerMap = new Map(existingLayers.map((layer) => [layer.layerId, layer]));
    const snapshot = buildDetachedSnapshot(args.comp);
    const nowSec = Math.floor(Date.now() / 1000);
    const schemaVersion = Number(parentData?.schemaVersion ?? 1) || 1;
    const userId = typeof parentData?.userId === "string" ? parentData.userId : "";
    const workspaceId = typeof parentData?.workspaceId === "string" ? parentData.workspaceId : "";

    await setDoc(
        compRef,
        {
            updatedAt: nowSec,
            canvasWidth: snapshot.canvasWidth,
            canvasHeight: snapshot.canvasHeight,
            backgroundColor: snapshot.backgroundColor,
            orderedLayerIds: snapshot.orderedLayerIds,
            isEmpty: false,
        },
        { merge: true },
    );

    let batch = writeBatch(db);
    let writes = 0;

    const flush = async () => {
        if (writes === 0) return;
        await batch.commit();
        batch = writeBatch(db);
        writes = 0;
    };

    const queueSet = async (ref: any, data: any, opts?: { merge?: boolean }) => {
        if (opts?.merge) {
            batch.set(ref, data, { merge: true });
        } else {
            batch.set(ref, data);
        }
        writes += 1;
        if (writes >= MAX_BATCH_WRITES) {
            await flush();
        }
    };

    const queueDelete = async (ref: any) => {
        batch.delete(ref);
        writes += 1;
        if (writes >= MAX_BATCH_WRITES) {
            await flush();
        }
    };

    const nextLayerIds = new Set(snapshot.layers.map((layer) => layer.layerId));

    for (const layer of snapshot.layers) {
        const existingLayer = existingLayerMap.get(layer.layerId);
        const layerRef = doc(compRef, kCompositionLayersSubcollectionName, layer.layerId);
        const nextClipIds = new Set(layer.orderedClipIds);

        await queueSet(
            layerRef,
            {
                id: layer.layerId,
                compositionId,
                userId,
                ...(workspaceId ? { workspaceId } : {}),
                schemaVersion,
                updatedAt: nowSec,
                ...(existingLayer ? {} : { createdAt: nowSec }),
                name: layer.name,
                type: layer.type,
                orderedClipIds: layer.orderedClipIds,
            },
            { merge: true },
        );

        for (const clipId of layer.orderedClipIds) {
            const clipRef = doc(layerRef, kCompositionLayerClipsSubcollectionName, clipId);
            const existingClipData = existingLayer?.clipsById.get(clipId) ?? {};
            const clipRecord = layer.clipRecordsById.get(clipId) ?? {};
            await queueSet(
                clipRef,
                {
                    ...existingClipData,
                    ...clipRecord,
                    id: clipId,
                    compositionId,
                    layerId: layer.layerId,
                    userId,
                    ...(workspaceId ? { workspaceId } : {}),
                    schemaVersion,
                    updatedAt: nowSec,
                    ...(existingLayer?.clipsById.has(clipId) ? {} : { createdAt: nowSec }),
                },
                { merge: false },
            );
        }

        for (const existingClipId of existingLayer?.clipIds ?? []) {
            if (nextClipIds.has(existingClipId)) continue;
            const existingClipRef = doc(layerRef, kCompositionLayerClipsSubcollectionName, existingClipId);
            await queueDelete(existingClipRef);
        }
    }

    for (const existingLayer of existingLayers) {
        if (nextLayerIds.has(existingLayer.layerId)) continue;
        const layerRef = doc(compRef, kCompositionLayersSubcollectionName, existingLayer.layerId);
        for (const clipId of existingLayer.clipIds) {
            const clipRef = doc(layerRef, kCompositionLayerClipsSubcollectionName, clipId);
            await queueDelete(clipRef);
        }
        await queueDelete(layerRef);
    }

    await flush();
};

const sanitizeCandidate = (
    candidate: ClipCandidate,
    fallbackIndex: number,
): ClipCandidate | null => {
    const startMs = clampPositiveMs(candidate?.startMs);
    const endMs = clampPositiveMs(candidate?.endMs);
    if (endMs <= startMs) return null;

    const title = String(candidate?.title ?? "").trim() || `Clip ${fallbackIndex + 1}`;
    const description = String(candidate?.description ?? "").trim();
    const previewText = String(candidate?.previewText ?? "").trim();
    const startUnitId = String(candidate?.startUnitId ?? "").trim() || `unit_${fallbackIndex + 1}_start`;
    const endUnitId = String(candidate?.endUnitId ?? "").trim() || startUnitId;

    return {
        id: String(candidate?.id ?? "").trim() || `candidate_${fallbackIndex + 1}`,
        title,
        description: description || previewText || "Generated social clip",
        startMs,
        endMs,
        startUnitId,
        endUnitId,
        previewText,
        score: Number.isFinite(Number(candidate?.score)) ? Number(candidate?.score) : undefined,
    };
};

export const materializeClipCandidatesToCompositions = async (args: {
    sourceCompositionId: string;
    sourceCompositionTitle?: string | null;
    settings: CreateClipsSettings;
    candidates: ClipCandidate[];
    transcriptUnits?: TranscriptUnit[];
    transcriptWords?: TranscriptTimingWord[];
    liveSeed?: DetachedExportSeed;
    duplicateComposition: DuplicateCompositionFn;
    deleteComposition?: (args: { compositionId: string }) => Promise<any>;
    resolveAssetUrl: ResolveAssetUrlFn;
    resolveEnhancedAudioUrl?: ResolveEnhancedAudioUrlFn;
}): Promise<CreatedClipComposition[]> => {
    const sourceCompositionId = String(args.sourceCompositionId ?? "").trim();
    if (!sourceCompositionId) throw new Error("Missing source composition");

    const liveSeed = args.liveSeed ?? buildDetachedExportSeed();
    const transcriptUnits = Array.isArray(args.transcriptUnits) ? args.transcriptUnits : [];
    const transcriptWords = Array.isArray(args.transcriptWords) ? args.transcriptWords : [];
    const created: CreatedClipComposition[] = [];

    for (let index = 0; index < args.candidates.length; index += 1) {
        const sanitized = sanitizeCandidate(args.candidates[index], index);
        if (!sanitized) continue;

        const titleOverride =
            sanitized.title ||
            `Clip of ${String(args.sourceCompositionTitle ?? "Untitled").trim() || "Untitled"}`;
        const newCompositionId = await args.duplicateComposition({
            compositionId: sourceCompositionId,
            titleOverride,
        });
        if (!newCompositionId) continue;

        let detachedComp: any | null = null;
        try {
            detachedComp = await createDetachedExportComposition({
                compositionId: sourceCompositionId,
                resolveAssetUrl: args.resolveAssetUrl,
                resolveEnhancedAudioUrl: args.resolveEnhancedAudioUrl,
                captionsWordsByClipId: liveSeed.captionsWordsByClipId,
                captionsConfigByClipId: liveSeed.captionsConfigByClipId,
                textConfigByClipId: liveSeed.textConfigByClipId,
                liveSnapshot: liveSeed.liveSnapshot,
            });

            const refinedRange = refineCreateClipRange({
                candidate: sanitized,
                transcriptUnits,
                transcriptWords,
            });

            trimDetachedCompositionToRange(
                detachedComp,
                refinedRange.startMs / 1000,
                refinedRange.endMs / 1000,
            );
            await applySocialClipTemplate(detachedComp, args.settings);
            await persistDetachedSnapshotToComposition({
                compositionId: newCompositionId,
                comp: detachedComp,
            });

            created.push({
                id: newCompositionId,
                compositionId: newCompositionId,
                candidateId: sanitized.id,
                title: sanitized.title,
                description: sanitized.description,
                durationSec: Math.max(
                    MIN_CLIP_DURATION_SEC,
                    Math.round((refinedRange.endMs - refinedRange.startMs) / 1000),
                ),
                startMs: refinedRange.startMs,
                endMs: refinedRange.endMs,
                previewText: sanitized.previewText,
                score: sanitized.score,
            });
        } catch (error) {
            try {
                await args.deleteComposition?.({ compositionId: newCompositionId });
            } catch {}
            if (created.length > 0 && args.deleteComposition) {
                await Promise.allSettled(
                    created.map((clip) =>
                        args.deleteComposition?.({ compositionId: clip.compositionId }),
                    ),
                );
            }
            throw error;
        } finally {
            try {
                await detachedComp?.dispose?.();
            } catch {}
        }
    }

    return created;
};
